/**
 * Preferencias de usuario persistidas localmente.
 *
 * Este e o unico lugar autorizado a escrever em localStorage. A regra de lint
 * `no-restricted-properties` bloqueia o uso direto em qualquer outro arquivo,
 * porque credencial nunca deve ser persistida no navegador (especificacao
 * V3.1, item 12.4 da V3).
 *
 * Preferencia visual nao e credencial, e pode ser persistida com seguranca.
 */

export type Tema = 'claro' | 'escuro' | 'sistema';

const CHAVE_TEMA = 'faponline.acoes.tema';

function ehTemaValido(valor: string | null): valor is Tema {
  return valor === 'claro' || valor === 'escuro' || valor === 'sistema';
}

/** Le o tema salvo. Retorna 'sistema' quando nao houver preferencia. */
export function lerTemaSalvo(): Tema {
  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    const salvo = window.localStorage.getItem(CHAVE_TEMA);

    return ehTemaValido(salvo) ? salvo : 'sistema';
  } catch {
    // Navegacao privada ou storage bloqueado por politica.
    return 'sistema';
  }
}

/** Salva o tema escolhido. Falha em silencio se o storage estiver indisponivel. */
export function salvarTema(tema: Tema): void {
  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    window.localStorage.setItem(CHAVE_TEMA, tema);
  } catch {
    // Preferencia visual nao vale interromper a aplicacao.
  }
}

/* ==========================================================================
   Marca de sessao
   ==========================================================================

   O refresh token vive em cookie HttpOnly, o que significa que JavaScript NAO
   consegue saber se ele existe. Sem alguma pista, a SPA teria de chamar
   /renovar em todo carregamento de pagina para descobrir, e falhar com 401 na
   maioria das vezes.

   Isso nao e apenas desperdicio. O limitador de taxa do servidor conta 30
   tentativas por 5 minutos POR ORIGEM, e os usuarios estao atras do NAT
   corporativo — chegam todos com o mesmo endereco publico. Uma renovacao por
   carregamento de pagina somaria a esse balde compartilhado e transformaria o
   inicio do expediente em 429 para todo mundo.

   A marca guarda apenas o INSTANTE DE EXPIRACAO da sessao. Nao e credencial:
   quem a ler nao consegue autenticar nada com ela, e ela nao serve para forjar
   sessao. E uma pista para decidir se vale a pena tentar.

   A marca pode divergir da realidade — cookie apagado pelo navegador, familia
   revogada no servidor. O pior caso e uma renovacao recusada, exatamente o que
   aconteceria sem ela.
   ========================================================================== */

const CHAVE_MARCA_DE_SESSAO = 'faponline.acoes.sessao.expiraEm';

/** Registra ate quando pode existir sessao para restaurar. */
export function salvarMarcaDeSessao(expiraEm: string): void {
  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    window.localStorage.setItem(CHAVE_MARCA_DE_SESSAO, expiraEm);
  } catch {
    // Storage bloqueado apenas custa uma renovacao a mais por carregamento.
  }
}

/** Apaga a marca. Chamado no logout e em toda renovacao recusada. */
export function limparMarcaDeSessao(): void {
  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    window.localStorage.removeItem(CHAVE_MARCA_DE_SESSAO);
  } catch {
    // Sem storage nao ha marca para apagar.
  }
}

/**
 * Indica se vale tentar restaurar a sessao.
 *
 * Falso quando nunca houve sessao neste navegador, quando houve logout, ou
 * quando o prazo registrado ja passou.
 */
export function podeExistirSessao(agora: Date = new Date()): boolean {
  let marca: string | null;

  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    marca = window.localStorage.getItem(CHAVE_MARCA_DE_SESSAO);
  } catch {
    // Sem acesso ao storage, tentar e a unica forma de descobrir.
    return true;
  }

  if (marca === null) {
    return false;
  }

  const expiraEm = new Date(marca.endsWith('Z') ? marca : `${marca}Z`);

  return !Number.isNaN(expiraEm.getTime()) && expiraEm > agora;
}

/* ==========================================================================
   Ordem das colunas da tabela de ações
   ========================================================================== */

const CHAVE_ORDEM_DAS_COLUNAS = 'faponline.acoes.ordemDasColunas';

/** Le a ordem salva, como texto JSON. Nulo quando nao houver preferencia. */
export function lerOrdemDasColunas(): string | null {
  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    return window.localStorage.getItem(CHAVE_ORDEM_DAS_COLUNAS);
  } catch {
    return null;
  }
}

/** Salva a ordem das colunas. Falha em silencio se o storage estiver indisponivel. */
export function salvarOrdemDasColunas(ordemEmJson: string): void {
  try {
    // eslint-disable-next-line no-restricted-syntax -- unico ponto autorizado
    window.localStorage.setItem(CHAVE_ORDEM_DAS_COLUNAS, ordemEmJson);
  } catch {
    // Preferencia visual nao vale interromper a aplicacao.
  }
}
